// ─────────────────────────────────────────────────────────────────────────────
// PRICE BOOK — the manufacturer / product-line / product-type pricing matrix.
//
// A window & door company buys from a handful of manufacturers, each of which
// sells a few product LINES, each of which is quoted as a set of product TYPES
// (single hung, roller, slider, french door…). What a type COSTS depends on
// what kind of thing it is — a single hung is one number, a slider is priced
// per configuration, a french door is a base unit plus whatever sidelites and
// transoms the opening needs. This file owns that shape.
//
// DATA-AS-CONFIG, like everything else the company can tailor (costCategories,
// leadSources, sheetSpecOptions, a preset's trimRates): nothing here is
// hardcoded product knowledge the app depends on. BoneFlow ships smart defaults
// — the manufacturers and lines a Florida impact company most likely uses, and
// the standard product types, all at $0 — and the company edits from there.
// Manufacturers get dropped and added as supplier relationships change, so a
// hardcoded list would be wrong within a year.
//
// ── WHERE IT LIVES ───────────────────────────────────────────────────────────
//   companies/{id}/meta/extras.priceBook = { manufacturers: [...] }
//
// An OBJECT wrapper rather than a bare array so book-level settings can be
// added later (a preferred manufacturer, a "prices reviewed on" stamp) without
// minting a second extras key. The manufacturers themselves are an ARRAY, which
// matters: setDoc(..., {merge:true}) deep-merges maps but REPLACES arrays, so a
// deleted manufacturer actually disappears. A map keyed by id would silently
// resurrect deletions — the classic Firestore merge trap.
//
// ── THE SHAPE ────────────────────────────────────────────────────────────────
//
//   Manufacturer { id, name, order, productLines: ProductLine[] }
//   ProductLine  { id, name, order, productTypes: ProductType[] }
//   ProductType  { id, name, order, pricingMode, notes,
//                  materialPrice, installPrice,            ← SIMPLE
//                  installRatePerPanel, orientations[],    ← PANEL / COMPONENT
//                  components[],                           ← COMPONENT
//                  extraFields[] }
//
// NESTED, not flat-with-parentIds (the marks model). A product line belongs to
// exactly one manufacturer for its whole life and is never reparented, so the
// flat model's one advantage — cheap global reordering — buys nothing, and
// nesting makes the whole manufacturer a single editable draft and a single
// atomic write.
//
// EVERY product type carries EVERY pricing field, defaulted. Switching a type's
// mode therefore never destroys what was already entered (switch to panel,
// think better of it, switch back — the simple prices are still there), and no
// reader has to null-check by mode.
//
// ── THE THREE PRICING MODES ──────────────────────────────────────────────────
//
// SIMPLE — one material price + one install price for the unit. A single hung
//   is a single hung; there is nothing to configure.
//
// PANEL-MULTIPLIED — horizontal rollers and sliding glass doors. Two different
//   rules on purpose, because the trade prices the two halves differently:
//     · INSTALL is genuinely per panel — one `installRatePerPanel` × the panel
//       count, because crew time scales with panels.
//     · MATERIAL is priced PER ORIENTATION, each with its own price, because a
//       manufacturer sells a finished XO unit at a unit price — it is not
//       "the panel price × 2". Deriving material from a per-panel rate would
//       be wrong in a way that compounds across a whole house.
//   The orientation LIST is company-editable per product type, because valid
//   configurations vary by manufacturer and line. Panel count is DERIVED from
//   the code (see panelCountFor) rather than typed, so it can never disagree
//   with the string the estimator reads.
//
// COMPONENT-ADDABLE — french doors and anything else sold as "a unit plus
//   pieces". The BASE unit is priced exactly like PANEL-MULTIPLIED (so a plain
//   double door with no extras needs no special handling at all), plus an
//   editable list of optional COMPONENTS, each with its own flat material and
//   install price.
//   FLAT PER COMPONENT IS A DELIBERATE SIMPLIFICATION: a 12" sidelite and a 24"
//   sidelite cost the same here. Per-inch component pricing is the obvious
//   future refinement if a company asks for it — it would be an additive change
//   (a `pricingBasis: 'flat' | 'perInch'` + a rate field on the component), not
//   a reshape, which is why the component is already its own object.
//
// ── DYNAMIC EXTRA FIELDS ─────────────────────────────────────────────────────
// Beyond Type / Width / Height, a product type can declare its own fields —
// "Leg Height" and "Shape" on an architectural window, whatever a company
// invents next. Declared as DATA on the product type, never as a switch in
// component code, so a new product type with new fields is a Settings edit.
//
//   ExtraField { id, key, label, fieldType: 'select'|'number'|'text',
//                options: string[], order }
//
// `key` is generated ONCE from the label and then FROZEN. Renaming the label
// must not change the key: a saved quote stores values under the key, and a
// key that moves would orphan every value already recorded against it.
//
// ORIENTATION IS NOT AN EXTRA FIELD. For panel/component types the orientation
// selector is implicit — it comes from `orientations`, which is load-bearing
// for the price. Modelling it a second time as an extraField would create two
// sources of truth for one answer. Anything that renders a product type's
// inputs should read: base fields, then the orientation picker when
// pricingMode !== 'simple', then extraFields.
//
// ── STATUS ───────────────────────────────────────────────────────────────────
// This file and its Settings UI are PROMPT A. Nothing here is wired into the
// Pricing tab yet; computeProductTypePrice() and flattenProductOptions() are
// the finished seams Prompt B plugs into.
// ─────────────────────────────────────────────────────────────────────────────

const rid = (n = 6) => Math.random().toString(36).slice(2, 2 + n);

export const newManufacturerId = () => `mfr_${Date.now().toString(36)}_${rid()}`;
export const newProductLineId = () => `pl_${Date.now().toString(36)}_${rid()}`;
export const newProductTypeId = () => `pt_${Date.now().toString(36)}_${rid()}`;
export const newOrientationId = () => `or_${Date.now().toString(36)}_${rid()}`;
export const newComponentId = () => `cmp_${Date.now().toString(36)}_${rid()}`;
export const newExtraFieldId = () => `ef_${Date.now().toString(36)}_${rid()}`;

const str = (v) => (v == null ? '' : String(v));
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ── Pricing modes ────────────────────────────────────────────────────────────

export const PRICING_MODES = ['simple', 'panel', 'component'];

export const PRICING_MODE_LABELS = {
  simple: 'Simple',
  panel: 'Panel-multiplied',
  component: 'Component-addable',
};

export const PRICING_MODE_DESCRIPTIONS = {
  simple: 'One material price and one install price for the unit. Single hungs, fixed windows — anything with nothing to configure.',
  panel: 'Install is a rate per panel; material is priced per orientation. Horizontal rollers and sliding glass doors.',
  component: 'A base unit priced like panel-multiplied, plus optional add-on pieces. French doors with sidelites and transoms.',
};

export function normalizePricingMode(v) {
  return PRICING_MODES.includes(v) ? v : 'simple';
}

export const EXTRA_FIELD_TYPES = ['text', 'number', 'select'];
export const EXTRA_FIELD_TYPE_LABELS = { text: 'Text', number: 'Number', select: 'Select' };

// ── Panel counting ───────────────────────────────────────────────────────────
//
// An orientation code is one character per panel — X operates, O is fixed. So
// XX is 2, OXO is 3, OXXO is 4. Counting only X/O characters keeps a code with
// a hand-written qualifier ("XX LH") honest; anything with no X or O at all
// falls back to its trimmed length rather than returning 0, so a company using
// some other notation still gets a number instead of a free unit.
//
// DERIVED, NEVER TYPED — the same rule as mark numbers. A stored panel count
// can drift from the code the estimator is reading; a derived one cannot. The
// editor shows the derived count live beside the code so a typo is visible
// immediately, and the count is ALSO stamped onto each saved orientation as a
// denormalized convenience for readers (never as the source of truth).
export function panelCountFor(code) {
  const s = str(code).trim();
  if (!s) return 0;
  const panels = (s.match(/[xo]/gi) || []).length;
  return panels || s.length;
}

// ── Extra-field keys ─────────────────────────────────────────────────────────
//
// Generated from the label at CREATION and then frozen (see the header note).
// camelCase, alphanumeric, de-duplicated within its product type.
export function extraFieldKeyFromLabel(label, existingKeys = []) {
  const words = str(label).trim().toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
  let base = words.length
    ? words[0] + words.slice(1).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')
    : 'field';
  if (/^[0-9]/.test(base)) base = `f${base}`;
  const taken = new Set(existingKeys);
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}${n}`)) n += 1;
  return `${base}${n}`;
}

// ── Constructors — every field defaulted, callers pass only what they know ───

export function createOrientation(partial = {}) {
  const code = str(partial.code).trim().toUpperCase();
  return {
    id: partial.id || newOrientationId(),
    code,
    panels: panelCountFor(code),
    materialPrice: num(partial.materialPrice),
  };
}

export function createComponent(partial = {}) {
  return {
    id: partial.id || newComponentId(),
    name: str(partial.name),
    materialPrice: num(partial.materialPrice),
    installPrice: num(partial.installPrice),
  };
}

export function createExtraField(partial = {}, existingKeys = []) {
  const label = str(partial.label);
  const fieldType = EXTRA_FIELD_TYPES.includes(partial.fieldType) ? partial.fieldType : 'text';
  return {
    id: partial.id || newExtraFieldId(),
    // An existing key is preserved verbatim — regenerating it on load would be
    // the orphaning bug the header warns about. A field with NO LABEL YET gets
    // NO KEY: minting one from a blank label would burn a meaningless key
    // ('field') the instant the row is added, and the real label — typed a
    // second later — could then never claim it.
    key: str(partial.key) || (label.trim() ? extraFieldKeyFromLabel(label, existingKeys) : ''),
    label,
    fieldType,
    options: fieldType === 'select' && Array.isArray(partial.options)
      ? partial.options.map(str).filter((o) => o.trim() !== '')
      : [],
    order: Number.isFinite(partial.order) ? partial.order : 0,
  };
}

export function createProductType(partial = {}) {
  const seenKeys = [];
  return {
    id: partial.id || newProductTypeId(),
    name: str(partial.name),
    order: Number.isFinite(partial.order) ? partial.order : 0,
    pricingMode: normalizePricingMode(partial.pricingMode),
    notes: str(partial.notes),

    // SIMPLE
    materialPrice: num(partial.materialPrice),
    installPrice: num(partial.installPrice),

    // PANEL-MULTIPLIED (and the base unit of COMPONENT-ADDABLE)
    installRatePerPanel: num(partial.installRatePerPanel),
    orientations: (Array.isArray(partial.orientations) ? partial.orientations : []).map(createOrientation),

    // COMPONENT-ADDABLE
    components: (Array.isArray(partial.components) ? partial.components : []).map(createComponent),

    extraFields: (Array.isArray(partial.extraFields) ? partial.extraFields : []).map((f, i) => {
      const built = createExtraField({ ...f, order: Number.isFinite(f.order) ? f.order : i }, seenKeys);
      seenKeys.push(built.key);
      return built;
    }),
  };
}

export function createProductLine(partial = {}) {
  return {
    id: partial.id || newProductLineId(),
    name: str(partial.name),
    order: Number.isFinite(partial.order) ? partial.order : 0,
    productTypes: (Array.isArray(partial.productTypes) ? partial.productTypes : [])
      .map((t, i) => createProductType({ ...t, order: Number.isFinite(t.order) ? t.order : i })),
  };
}

export function createManufacturer(partial = {}) {
  return {
    id: partial.id || newManufacturerId(),
    name: str(partial.name),
    order: Number.isFinite(partial.order) ? partial.order : 0,
    productLines: (Array.isArray(partial.productLines) ? partial.productLines : [])
      .map((l, i) => createProductLine({ ...l, order: Number.isFinite(l.order) ? l.order : i })),
  };
}

// ── Reading ──────────────────────────────────────────────────────────────────

// Defensive read-time normalization, in the spirit of marksFromSavedDoc: hand
// it anything (undefined, a bare array from some future shape change, a
// half-written document) and get a well-formed book back. Order is re-stamped
// dense from array position, so array order is the single ordering truth.
export function normalizePriceBook(raw) {
  const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.manufacturers) ? raw.manufacturers : []);
  return {
    manufacturers: list.filter(Boolean).map((m, i) => createManufacturer({ ...m, order: i })),
  };
}

// Seed ONLY when the key has never been written. An EMPTY book is a legitimate,
// respected state — a company that deleted every manufacturer meant it, and
// re-seeding on the next load would be the "deleted entries come back" problem
// flagged for the internal-company seeder. That is the deliberate difference
// from costCategoriesNeedSeed, which treats empty as unseeded.
export function priceBookNeedsSeed(raw) {
  return !raw || !Array.isArray(raw.manufacturers);
}

export function findManufacturer(priceBook, manufacturerId) {
  if (!manufacturerId) return null;
  return (priceBook?.manufacturers || []).find((m) => m.id === manufacturerId) || null;
}

export function findProductLine(priceBook, manufacturerId, lineId) {
  const m = findManufacturer(priceBook, manufacturerId);
  if (!m || !lineId) return null;
  return (m.productLines || []).find((l) => l.id === lineId) || null;
}

export function findProductType(priceBook, manufacturerId, lineId, typeId) {
  const l = findProductLine(priceBook, manufacturerId, lineId);
  if (!l || !typeId) return null;
  return (l.productTypes || []).find((t) => t.id === typeId) || null;
}

export function countProductTypes(manufacturer) {
  return (manufacturer?.productLines || []).reduce((n, l) => n + (l.productTypes || []).length, 0);
}

// ── The Prompt B seam ────────────────────────────────────────────────────────
// Nothing below is wired into the Pricing tab yet. Both are pure and exist so
// the tab integration is a consumption job, not a modelling job.

// Every quotable product as one flat list — the shape a typeahead wants.
// `label` reads "PGT WinGuard — Sliding Glass Door"; the ids are what a mark
// would store so the row can be re-priced later even if names change.
export function flattenProductOptions(priceBook) {
  const out = [];
  (priceBook?.manufacturers || []).forEach((m) => {
    (m.productLines || []).forEach((l) => {
      (l.productTypes || []).forEach((t) => {
        out.push({
          manufacturerId: m.id,
          manufacturerName: m.name,
          productLineId: l.id,
          productLineName: l.name,
          productTypeId: t.id,
          productTypeName: t.name,
          pricingMode: t.pricingMode,
          label: `${m.name} ${l.name} — ${t.name}`.trim(),
          productType: t,
        });
      });
    });
  });
  return out;
}

// What one unit of this product type costs, given the choices made on a row.
// Returns internal COST (material + install); markup stays where it already
// lives, on the sheet. `breakdown` is there so a row can show its arithmetic
// the way the trim line does.
export function computeProductTypePrice({
  productType,
  orientationCode = '',
  componentIds = [],
  quantity = 1,
} = {}) {
  const t = productType;
  const qty = Math.max(0, num(quantity) || 0);
  const empty = { material: 0, install: 0, total: 0, panels: 0, orientation: null, components: [], breakdown: [] };
  if (!t) return empty;

  const mode = normalizePricingMode(t.pricingMode);
  const breakdown = [];
  let material = 0;
  let install = 0;
  let panels = 0;
  let orientation = null;
  const components = [];

  if (mode === 'simple') {
    material = num(t.materialPrice);
    install = num(t.installPrice);
    if (material) breakdown.push({ label: 'Material', amount: material });
    if (install) breakdown.push({ label: 'Install', amount: install });
  } else {
    // Panel-multiplied base — shared by 'panel' and 'component'.
    const wanted = str(orientationCode).trim().toUpperCase();
    orientation = (t.orientations || []).find((o) => str(o.code).trim().toUpperCase() === wanted) || null;
    panels = orientation ? panelCountFor(orientation.code) : panelCountFor(wanted);
    material = orientation ? num(orientation.materialPrice) : 0;
    install = num(t.installRatePerPanel) * panels;
    if (material) breakdown.push({ label: `Material (${orientation ? orientation.code : wanted || 'no orientation'})`, amount: material });
    if (install) breakdown.push({ label: `Install (${panels} panel${panels === 1 ? '' : 's'})`, amount: install });

    if (mode === 'component') {
      const wantedIds = new Set(componentIds || []);
      (t.components || []).filter((c) => wantedIds.has(c.id)).forEach((c) => {
        const cm = num(c.materialPrice);
        const ci = num(c.installPrice);
        material += cm;
        install += ci;
        components.push({ id: c.id, name: c.name, materialPrice: cm, installPrice: ci });
        if (cm || ci) breakdown.push({ label: c.name || 'Component', amount: cm + ci });
      });
    }
  }

  const unit = material + install;
  return {
    material: material * qty,
    install: install * qty,
    total: unit * qty,
    unitTotal: unit,
    panels,
    orientation,
    components,
    breakdown,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHIPPED DEFAULTS
//
// The manufacturers and lines a Florida impact window & door company most
// likely starts from, and the product types every one of them quotes. ALL
// PRICES ARE ZERO ON PURPOSE — real numbers are the company's own commercial
// data and inventing plausible ones would be worse than leaving them blank,
// because a plausible wrong number gets quoted.
//
// Ids are readable and deterministic (mfr_pgt / pl_pgt_winguard /
// pt_pgt_winguard_singleHung) rather than timestamped: this seed runs once per
// company, and readable ids make the stored document legible and a re-seed
// idempotent. Anything the company adds later gets a random id.
// ─────────────────────────────────────────────────────────────────────────────

const ARCH_SHAPES = ['Half Round', 'Quarter Round', 'Eyebrow', 'Octagon', 'Trapezoid', 'Triangle', 'Custom'];

// Standard orientation sets. These are configurations, not prices — real trade
// vocabulary, and every one of them stays editable per product type.
const ROLLER_2_ORIENTATIONS = ['XX', 'XO', 'OX'];
const ROLLER_3_ORIENTATIONS = ['XOX', 'OXO', 'XXX'];
const SLIDER_ORIENTATIONS = ['XX', 'XXX', 'OXO', 'OXXO', 'XXXX'];
const FRENCH_DOOR_ORIENTATIONS = ['X', 'XX'];

const FRENCH_DOOR_COMPONENTS = ['Left Sidelite', 'Right Sidelite', 'Transom'];

// Estimating guidance shown in the Settings editor wherever orientations are
// priced. Lives here as the single wording, rendered by the UI — deliberately
// NOT seeded into each product type's `notes`, which stays the company's own
// free-text field rather than a copy of our advice.
export const PANEL_PRICING_GUIDANCE = 'Price each orientation toward the HIGH end of what the manufacturer quotes for that configuration. Across a whole house an under-priced unit compounds; a small margin on each one absorbs the surprises.';

// One fresh set of standard product types, built per product line so no two
// lines share an object.
function buildStandardProductTypes(mfrSlug, lineSlug) {
  const pid = (typeSlug) => `pt_${mfrSlug}_${lineSlug}_${typeSlug}`;
  const orientations = (prefix, codes) => codes.map((code) => createOrientation({
    id: `or_${prefix}_${code.toLowerCase()}`,
    code,
    materialPrice: 0,
  }));

  return [
    createProductType({
      id: pid('singleHung'),
      name: 'Single Hung',
      pricingMode: 'simple',
      order: 0,
    }),
    createProductType({
      id: pid('roller2'),
      name: 'Horizontal Roller (2-Panel)',
      pricingMode: 'panel',
      orientations: orientations(`${mfrSlug}_${lineSlug}_r2`, ROLLER_2_ORIENTATIONS),
      order: 1,
    }),
    createProductType({
      id: pid('roller3'),
      name: 'Horizontal Roller (3-Panel)',
      pricingMode: 'panel',
      orientations: orientations(`${mfrSlug}_${lineSlug}_r3`, ROLLER_3_ORIENTATIONS),
      order: 2,
    }),
    createProductType({
      id: pid('slidingGlassDoor'),
      name: 'Sliding Glass Door',
      pricingMode: 'panel',
      orientations: orientations(`${mfrSlug}_${lineSlug}_sgd`, SLIDER_ORIENTATIONS),
      order: 3,
    }),
    createProductType({
      id: pid('frenchDoor'),
      name: 'French Door',
      pricingMode: 'component',
      orientations: orientations(`${mfrSlug}_${lineSlug}_fd`, FRENCH_DOOR_ORIENTATIONS),
      components: FRENCH_DOOR_COMPONENTS.map((name, i) => createComponent({
        id: `cmp_${mfrSlug}_${lineSlug}_fd_${i}`,
        name,
        materialPrice: 0,
        installPrice: 0,
      })),
      order: 4,
    }),
    createProductType({
      id: pid('architectural'),
      name: 'Architectural Window',
      pricingMode: 'simple',
      // The one seeded type that ships extra fields — the concrete example of
      // why extraFields exist at all.
      extraFields: [
        createExtraField({ id: `ef_${mfrSlug}_${lineSlug}_leg`, key: 'legHeight', label: 'Leg Height', fieldType: 'number', order: 0 }),
        createExtraField({ id: `ef_${mfrSlug}_${lineSlug}_shape`, key: 'shape', label: 'Shape', fieldType: 'select', options: ARCH_SHAPES, order: 1 }),
      ],
      order: 5,
    }),
    createProductType({
      id: pid('other'),
      name: 'Other',
      pricingMode: 'simple',
      order: 6,
    }),
  ];
}

const DEFAULT_BOOK = [
  { slug: 'pgt', name: 'PGT', lines: [{ slug: 'winguard', name: 'WinGuard' }, { slug: 'sparta', name: 'Sparta' }] },
  { slug: 'es', name: 'ES', lines: [{ slug: 'elite', name: 'Elite' }, { slug: 'prestige', name: 'Prestige' }, { slug: 'multimax', name: 'Multimax' }] },
  { slug: 'cws', name: 'CWS', lines: [{ slug: 'hurricaneguard', name: 'Hurricane Guard' }] },
];

export function buildDefaultPriceBook() {
  return {
    manufacturers: DEFAULT_BOOK.map((m, mi) => createManufacturer({
      id: `mfr_${m.slug}`,
      name: m.name,
      order: mi,
      productLines: m.lines.map((l, li) => createProductLine({
        id: `pl_${m.slug}_${l.slug}`,
        name: l.name,
        order: li,
        productTypes: buildStandardProductTypes(m.slug, l.slug),
      })),
    })),
  };
}

// A company in a vertical that does not buy manufactured product (BoneFlow's
// own SaaS tenant) gets a real, empty book rather than window-and-door
// defaults it would only have to delete.
export function buildEmptyPriceBook() {
  return { manufacturers: [] };
}
